"use client"
import styled, { keyframes } from "styled-components"
import { motion } from "framer-motion"
import { useInView } from "react-intersection-observer"
import { FiCalendar, FiMapPin, FiBriefcase } from "react-icons/fi"

// --- STYLED COMPONENTS ---

const ExperienceSection = styled.section`
  padding: 6rem 0;
  background: transparent; // To see the global gradient
  position: relative;
`

const Container = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 1.5rem;
`

const SectionHeader = styled(motion.div)`
  text-align: center;
  margin-bottom: 4rem;

  h2 {
    font-size: clamp(2.5rem, 5vw, 3.5rem);
    font-weight: 700;
    color: #1B1B1E;
    margin-bottom: 1rem;
    font-family: 'Poppins', sans-serif;

    .highlight {
      background: linear-gradient(135deg, #2A9D8F, #2A9D8F); // Gold to orange gradient
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
    }
  }

  p {
    font-size: 1.125rem;
    color: #1B1B1E;
    opacity: 0.8;
    max-width: 650px;
    margin: 0 auto;
    line-height: 1.6;
  }
`

// The main timeline container
const TimelineContainer = styled.div`
  position: relative;
  max-width: 900px;
  margin: 0 auto;

  // The vertical line running down the middle
  &::after {
    content: '';
    position: absolute;
    width: 3px;
    background-color: rgba(38, 70, 83, 0.2);
    top: 0;
    bottom: 0;
    left: 50%;
    margin-left: -1.5px;
    
    @media (max-width: 768px) {
      left: 30px;
    }
  }
`

// Wrapper for each individual experience item
const TimelineItem = styled(motion.div)`
  padding: 1rem 3rem;
  position: relative;
  width: 50%;
  
  // Align items to the left and right
  &:nth-child(odd) {
    left: 0;
  }
  
  &:nth-child(even) {
    left: 50%;
  }

  // Mobile layout: stack everything on one side
  @media (max-width: 768px) {
    width: 100%;
    padding-left: 70px;
    padding-right: 15px;
    left: 0 !important;
  }
`

const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.1); }
  100% { transform: scale(1); }
`

// The dot on the timeline
const TimelineDot = styled.div`
  content: '';
  position: absolute;
  width: 20px;
  height: 20px;
  background-color: #F4F1DE; // Light Beige
  border: 4px solid #2A9D8F; // Teal border
  top: 32px;
  right: -10px;
  border-radius: 50%;
  z-index: 1;
  
  animation: ${pulse} 2s ease-in-out infinite;

  ${TimelineItem}:nth-child(even) & {
    left: -10px;
  }

  @media (max-width: 768px) {
    left: 20px;
  }
`

// The content card for each experience
const TimelineContent = styled.div`
  padding: 1.5rem 2rem;
  background: rgba(255, 255, 255, 0.6);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(38, 70, 83, 0.15);
  position: relative;
  border-radius: 1rem;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.08);
  }

  h3 {
    font-size: 1.375rem;
    font-weight: 600;
    color: #1B1B1E;
    margin-bottom: 0.25rem;
  }

  .company {
    color: #264653;
    font-weight: 500;
    margin-bottom: 0.75rem;
    font-size: 1rem;
  }

  .meta {
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;
    margin-bottom: 1rem;
    color: #1B1B1E;
    opacity: 0.7;
    font-size: 0.875rem;

    .meta-item {
      display: flex;
      align-items: center;
      gap: 0.4rem;
    }
  }

  .description {
    color: #1B1B1E;
    opacity: 0.9;
    line-height: 1.6;
    margin-bottom: 1rem;
    font-size: 0.95rem;
    padding-left: 1rem;

    li {
      margin-bottom: 0.5rem;
    }
  }

  .skills {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;

    span {
      padding: 0.25rem 0.75rem;
      background: rgba(42, 157, 143, 0.15); // Teal with low opacity
      color: #2A9D8F; // Teal
      font-size: 0.75rem;
      font-weight: 500;
      border-radius: 9999px;
    }
  }
`

// --- DATA ---

const experiences = [
  {
    title: "Full Stack Developer",
    company: "Klab Rwanda",
    location: "Kigali, Rwanda",
    period: "2024 - 4 months intensive",
    type: "Certification",
    description: [
      "Completed an intensive certification program covering the entire MERN stack.",
      "Mastered modern web technologies, agile methodologies, and best practices in software architecture.",
      "Built and deployed multiple full-stack projects, from initial concept to production-ready applications."
    ],
    skills: ["React", "Node.js", "Express.js", "MongoDB", "JavaScript"],
  },
  {
    title: "Backend Developer ",
    company: "SheCanCode Program",
    location: "Kigali, Rwanda",
    period: "2024",
    type: "Certification",
    description: [
      "Focused on backend architecture for various web applications.",
      "Designed, developed, and documented RESTful APIs for client-side consumption.",
      "Wrote optimized SQL queries and designed robust database schemas for performance and scalability."
    ],
    skills: ["Node.js", "Express.js", "MongoDB", "JavaScript", "RESTful APIs","Swagger API Documentation", "Database Design", "Testing"],
  },
];

// --- MAIN COMPONENT ---

const Experience = () => {
  const [ref, inView] = useInView({
    threshold: 0.2,
    triggerOnce: true,
  });

  const fadeInUp = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
  };

  return (
    <ExperienceSection id="experience" ref={ref}>
      <Container>
        <SectionHeader variants={fadeInUp} initial="hidden" animate={inView ? "visible" : "hidden"}>
          <h2>
            Work <span className="highlight">Experience</span>
          </h2>
          <p>My professional journey and key accomplishments in the world of software development.</p>
        </SectionHeader>

        <TimelineContainer>
          {experiences.map((exp, index) => (
            <TimelineItem key={index} variants={fadeInUp} initial="hidden" animate={inView ? "visible" : "hidden"}>
              <TimelineDot />
              <TimelineContent>
                <h3>{exp.title}</h3>
                <div className="company">@ {exp.company}</div>
                <div className="meta">
                  <div className="meta-item"><FiCalendar size={16} /><span>{exp.period}</span></div>
                  <div className="meta-item"><FiMapPin size={16} /><span>{exp.location}</span></div>
                  <div className="meta-item"><FiBriefcase size={16} /><span>{exp.type}</span></div>
                </div>
                <ul className="description">
                  {exp.description.map((point, idx) => (
                    <li key={idx}>{point}</li>
                  ))}
                </ul>
                <div className="skills">
                  {exp.skills.map((skill, idx) => (
                    <span key={idx}>{skill}</span>
                  ))}
                </div>
              </TimelineContent>
            </TimelineItem>
          ))}
        </TimelineContainer>
      </Container>
    </ExperienceSection>
  )
}

export default Experience;